import type { PlaceholderProps } from './types';

export const DEFAULT_PLACEHOLDER_REGEX = /%(.*?)%/gi;

/**
 * Placeholder component which takes a placeholder text and converts it into a span element where the placeholder content is
 * replaced by links or spans if needed as children.
 *
 * @param props - Props for the Placeholder component.
 * @returns The rendered Placeholder component with the placeholder content replaced by links or spans.
 */
export function Placeholder(props: PlaceholderProps) {
  const linkTarget = props.openInNewTab ? '_blank' : '';
  const defaultCondition = props.regex || DEFAULT_PLACEHOLDER_REGEX;

  // Extract all placeholders from the content without the surrounding characters (e. g. {{test}} => test) and collect
  // them in an array
  // Important: Do not use .test() before this loop - it advances regex lastIndex and breaks match detection
  let searchResult;
  const placeholders: string[] = [];

  while ((searchResult = defaultCondition.exec(props.placeholderContent)) !== null) {
    placeholders.push(searchResult[1]);
  }

  // If no placeholders found, return original content
  if (placeholders.length === 0) {
    return <span className="placeholder">{props.placeholderContent}</span>;
  }

  // Split the content into parts based on regular expression and replace placeholders with link or span elements if needed
  // by iterating over the parts array
  const parts = props.placeholderContent.split(defaultCondition);
  const elements = parts.map((part: string, index: number) => {
    if (placeholders.includes(part) && props.href) {
      return (
        <a
          className="placeholder__link"
          href={props.href}
          key={index}
          rel="noreferrer"
          target={linkTarget}
          title={props.contentToReplaceWith}
        >
          {props.contentToReplaceWith}
        </a>
      );
    } else if (placeholders.includes(part) && !props.href) {
      return (
        <span className="placeholder__text" key={index}>
          {props.contentToReplaceWith}
        </span>
      );
    } else {
      return (
        <span className="placeholder__text" key={index}>
          {part}
        </span>
      );
    }
  });

  return <span className="placeholder">{elements}</span>;
}
